/** Game state pushed from WASM each frame via window.__gameState. */
export interface GameState {
  mode: 'title' | 'menu' | 'play' | 'complete' | 'locked';

  // Menu
  menuSelection: number;
  hasSave: boolean;

  // Play
  stageName: string;
  stageIndex: number;
  canRotate: boolean;
  canShift: boolean;
  stageCompleted: boolean;
  stageMode: 'playing' | 'shifting';
  canGoPrev: boolean;
  canGoNext: boolean;
  stageProgressed: boolean;
  isAnimating: boolean;
  fadeAlpha: number;
  isVictory: boolean;
  messageKey: string | null;

  // Overlays
  touchMenuOpen: boolean;
  touchControlsOpen: boolean;
  desktopControlsOpen: boolean;
  commentModalOpen: boolean;
  commentAutoShown: boolean;
  confirmReset: boolean;
  confirmSelection: number;

  // Device
  isTouch: boolean;
  aspect: number;
  gameTime: number;
}

const DEFAULTS: GameState = {
  mode: 'title',
  menuSelection: 0,
  hasSave: false,
  stageName: '',
  stageIndex: 0,
  canRotate: false,
  canShift: false,
  stageCompleted: false,
  stageMode: 'playing',
  canGoPrev: false,
  canGoNext: false,
  stageProgressed: false,
  isAnimating: false,
  fadeAlpha: 0,
  isVictory: false,
  messageKey: null,
  touchMenuOpen: false,
  touchControlsOpen: false,
  desktopControlsOpen: false,
  commentModalOpen: false,
  commentAutoShown: false,
  confirmReset: false,
  confirmSelection: 0,
  isTouch: false,
  aspect: 4 / 3,
  gameTime: 0,
};

type Listener = () => void;

/**
 * Reads game state from the WASM-pushed global object each frame
 * and notifies subscribers when it changes.
 */
class GameBridge {
  private _state: GameState = { ...DEFAULTS };
  private _listeners = new Set<Listener>();
  private _raf: number | null = null;
  private _lastMode: string = '';

  get state(): Readonly<GameState> {
    return this._state;
  }

  start() {
    const tick = () => {
      const raw = (window as unknown as Record<string, unknown>).__gameState as
        | Record<string, unknown>
        | undefined;
      if (raw) {
        Object.assign(this._state, raw);

        // Notify listeners (every frame while game is running)
        for (const cb of this._listeners) {
          cb();
        }
      }
      this._raf = requestAnimationFrame(tick);
    };
    this._raf = requestAnimationFrame(tick);
  }

  stop() {
    if (this._raf !== null) {
      cancelAnimationFrame(this._raf);
      this._raf = null;
    }
  }

  subscribe(cb: Listener): () => void {
    this._listeners.add(cb);
    return () => this._listeners.delete(cb);
  }
}

export const bridge = new GameBridge();
